/**
 * מרכז התראות — "מה חדש, מה עדיין פתוח, ומה כבר טופל" (spec-business §4.8).
 * Grouped by day; a row's title toggles the body; "בטל" dismisses with an undo toast.
 * "סמן הכול כנקרא" zeroes the bell but never removes anything from "דורש טיפול" (§5).
 */

import { useState } from 'react';
import type { Alert, AlertFilter, AlertsResponse, SummaryResponse } from '../../../api-types.ts';
import { relTime } from '../format.ts';
import { daysBetween } from '../dates.ts';
import { ALERT_GLYPH, he } from '../i18n/he.ts';
import { CategoryIcon } from '../../../../shared/ui/src/index.tsx';
import type { GlyphName } from '../../../../shared/ui/src/index.tsx';
import { Icon } from '../components/Icon.tsx';
import { alertTarget } from '../components/Shell.tsx';
import { Card, CardHead, ChipScroller, Chip, EmptyState, FilterChip, Skeleton } from '../components/ui.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { cacheKey } from '../hooks/cache.ts';
import { useQuery } from '../hooks/useQuery.ts';
import { Link, useRouter } from '../hooks/useRouter.tsx';
import { errorMessage } from '../api-select.ts';

const FILTERS: Array<[AlertFilter, string]> = [
  ['all', he.alerts.filterAll],
  ['unread', he.alerts.filterUnread],
  ['critical', he.alerts.filterCritical],
  ['overdue', he.alerts.filterOverdue],
  ['tax', he.alerts.filterTax],
  ['ops', he.alerts.filterOps],
];

function dayGroup(createdAt: string, asOf: string): string {
  const day = createdAt.slice(0, 10);
  const diff = daysBetween(day, asOf);
  if (diff <= 0) return he.alerts.today;
  if (diff === 1) return he.alerts.yesterday;
  if (diff <= 7) return he.alerts.thisWeek;
  return he.alerts.earlier;
}

export function AlertsScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, toast } = useApp();
  const { route } = useRouter();
  const [filter, setFilter] = useState<AlertFilter>((route.query.filter as AlertFilter) ?? 'all');
  const [open, setOpen] = useState<string | null>(null);
  const alerts = useQuery<AlertsResponse>(cacheKey('alerts', { filter }), () => api.alerts({ filter, includeDismissed: filter === 'all' ? '1' : '0' }), {
    scopes: ['summary'],
  });
  const asOf = summary?.asOf ?? '';
  const items = alerts.data?.items ?? [];

  const groups: Array<[string, Alert[]]> = [];
  for (const alert of items) {
    const name = dayGroup(alert.createdAt, asOf);
    const group = groups.find(([key]) => key === name);
    if (group) group[1].push(alert);
    else groups.push([name, [alert]]);
  }

  const dismiss = (alert: Alert) => {
    api.updateAlert(alert.id, { dismissed: true }).then(
      () =>
        toast(he.alerts.dismissed, {
          action: { label: he.alerts.undo, onClick: () => void api.updateAlert(alert.id, { dismissed: false }).catch(() => undefined) },
        }),
      (error: unknown) => toast(errorMessage(error), { error: true }),
    );
  };

  return (
    <div className="grid">
      <Card span={12} labelledBy="h-al">
        <CardHead
          titleId="h-al"
          title={he.alerts.title}
          sub={he.alerts.sub(alerts.data?.unreadCount ?? 0, alerts.data?.openCount ?? 0)}
          actions={
            <button type="button" className="btn sm" onClick={() => void api.readAllAlerts({ filter }).catch(() => undefined)}>
              <Icon name="checkCircle" />
              {he.alerts.readAll}
            </button>
          }
        />
        <ChipScroller>
          {FILTERS.map(([key, label]) => (
            <FilterChip
              key={key}
              label={label}
              count={key === 'unread' ? alerts.data?.unreadCount : undefined}
              pressed={filter === key}
              onClick={() => setFilter(key)}
            />
          ))}
        </ChipScroller>

        {alerts.loading && !alerts.data ? (
          <Skeleton className="ch" />
        ) : groups.length ? (
          groups.map(([name, rows]) => (
            <div key={name}>
              <div className="kicker" style={{ paddingTop: '6px' }}>
                {name}
              </div>
              <div className="rows">
                {rows.map((alert) => {
                  const unread = !alert.readAt && !alert.dismissedAt;
                  return (
                    <div className={`alert-row ${alert.severity}${unread ? ' unread' : ''}${open === alert.id ? ' open' : ''}`} key={alert.id}>
                      <span className="sev" aria-hidden="true" />
                      <span className="aic" aria-hidden="true">
                        <CategoryIcon
                          glyph={ALERT_GLYPH[alert.type] as GlyphName}
                          soft
                          size={18}
                          hue={alert.severity === 'critical' || alert.severity === 'warn' ? 'warn' : undefined}
                        />
                      </span>
                      <div className="ab">
                        <button
                          type="button"
                          className="at"
                          aria-expanded={open === alert.id}
                          aria-controls={`ad-${alert.id}`}
                          onClick={() => {
                            setOpen(open === alert.id ? null : alert.id);
                            if (unread) void api.updateAlert(alert.id, { read: true }).catch(() => undefined);
                          }}
                        >
                          {alert.title}
                        </button>
                        <div className="ad" id={`ad-${alert.id}`}>
                          {alert.body}
                        </div>
                        <div className="am">
                          <span>{relTime(alert.createdAt)}</span>
                          {alert.dismissedAt ? (
                            <Chip tone="good" icon="check" small>
                              {alert.resolvedNote ?? he.alerts.handled}
                            </Chip>
                          ) : null}
                          <Link className="link-btn" to={alertTarget(alert)} style={{ fontSize: '12px' }}>
                            {alert.actionLabel} <Icon name="arrowLeft" />
                          </Link>
                        </div>
                      </div>
                      <div className="ax">
                        {unread ? <span className="dot" aria-label={he.alerts.unreadDot} /> : null}
                        {!alert.dismissedAt ? (
                          <button type="button" className="btn icon sm" aria-label={he.alerts.dismiss} onClick={() => dismiss(alert)}>
                            <Icon name="x" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <EmptyState icon="bell" text={he.alerts.empty} />
        )}
      </Card>
    </div>
  );
}
